import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import environment from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/environment.json";
import HttpClientV2, { HttpClientV2Config } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { LazoLoginResponse } from "@/types/LazoLoginResponse";
import { LoginOneTimeCodeExchangeDTO } from "@/types/LoginOneTimeCodeExchangeDTO";
import { SessionTokenDTO } from "@/types/SessionTokenDTO";

// In Dev the page lives on https://dev.lazo.one:8443 but the submodule's
// default HttpClientV2 baseURL points at http://localhost:8080, which a
// page loaded over HTTPS cannot reach (mixed-content + cross-origin).
// Override baseURL to "" so axios resolves the URL against the page origin;
// Caddy then proxies /api/* to the Spring backend, and the Set-Cookie that
// issues lazo_auth_token (Domain=.lazo.one) is honoured by the browser.
//
// In QA/Prod the bundled baseURL already points at the real Spring host
// (qa-api.lazo.one / api.lazo.one) which lives outside CloudFront and
// handles the API directly. Keep the bundled value there — overriding to
// "" would route POSTs through the marketing CloudFront distributions,
// which only allow GET/HEAD and would 403 every login attempt.
const IS_DEV = String((environment as { env?: string }).env ?? "").toLowerCase() === "dev";
const DEV_AUTH_CONFIG: HttpClientV2Config | undefined = IS_DEV ? { axiosConfig: { baseURL: "" } } : undefined;

// /login, /otc and /session are the auth calls whose cookie the browser must honour: /login and
// /otc issue the Domain=.lazo.one lazo_auth_token cookie, and /session reads it back. A cross-origin
// XHR (app.lazo.one -> api.lazo.one) only sends/honours that cookie when the request is in
// credentials mode, so these calls opt in with withCredentials. Scoped here on purpose — the shared
// HttpClientV2 stays credential-less so every other request keeps using the bearer token.
const LOGIN_AUTH_CONFIG: HttpClientV2Config = {
  axiosConfig: {
    ...(IS_DEV ? { baseURL: "" } : {}),
    withCredentials: true,
  },
};

// The OTC exchange is single-use: the server consumes the code the moment it reads it. If it runs
// long enough to hit the shared 5s default timeout, the code is burned but the client never sees the
// token — stranding the user on a second login. Give it the same 25s headroom as getUserByToken so a
// cold cross-subdomain round-trip resolves instead of timing out. (The /session cookie-recovery
// fallback still backstops a genuine failure; this just makes that fallback rarely necessary.)
//
// It also sends withCredentials so the OTC exchange's Set-Cookie persists: this matters for
// cross-subdomain sessions (e.g. impersonation / platform.lazo.one) — without credentials the cookie
// is silently dropped, so the .lazo.one cookie keeps the previous login's identity and sibling
// subdomains never see the newly-exchanged user.
const OTC_EXCHANGE_TIMEOUT_MS = 25000;
const OTC_AUTH_CONFIG: HttpClientV2Config = {
  axiosConfig: {
    ...(IS_DEV ? { baseURL: "" } : {}),
    withCredentials: true,
    timeout: OTC_EXCHANGE_TIMEOUT_MS,
  },
};

class LazoAuthService {
  private path: string;

  constructor(path: string) {
    this.path = path;
  }

  getRedirectUrl(userExternalId: string, token: string): Promise<ApiResponseV2<LazoLoginResponse>> {
    return HttpClientV2.post(`${this.path}/redirect-url`, { userExternalId, token }, DEV_AUTH_CONFIG);
  }

  validateLogin(email: string, password: string): Promise<ApiResponseV2<LazoLoginResponse>> {
    return HttpClientV2.post(`${this.path}/login`, { email, password }, LOGIN_AUTH_CONFIG);
  }

  workspaceRedirect(token: string, expiresAt: Date): Promise<ApiResponseV2<LazoLoginResponse>> {
    return HttpClientV2.post(`${this.path}/workspace-redirect`, { token, expiresAt }, DEV_AUTH_CONFIG);
  }

  requestMagicLink(email: string, redirect?: string): Promise<ApiResponseV2<void>> {
    return HttpClientV2.post(`${this.path}/request-magic-link`, { email, redirect }, DEV_AUTH_CONFIG);
  }

  exchangeOTC(otc: string): Promise<ApiResponseV2<LoginOneTimeCodeExchangeDTO>> {
    return HttpClientV2.post(`${this.path}/otc`, { otc }, OTC_AUTH_CONFIG);
  }

  /**
   * Recovers the current session token from the HttpOnly `lazo_auth_token` cookie (Domain=.lazo.one)
   * that /login and /otc set. Used as a fallback when an OTC exchange fails after the server already
   * consumed the code and set the cookie (e.g. a timeout swallowed the JSON body): the cookie is
   * still valid, so the *.lazo.one subdomain can re-derive its bearer token without a second login.
   *
   * Sent in credentials mode for the same reason as /login — a cross-origin XHR
   * (app.lazo.one -> api.lazo.one) only attaches the cookie when withCredentials is set. Returns 401
   * (mapped to a non-success ApiResponseV2) on every failure path, so callers branch on isSuccess().
   */
  getSession(): Promise<ApiResponseV2<SessionTokenDTO>> {
    return HttpClientV2.get(`${this.path}/session`, LOGIN_AUTH_CONFIG);
  }

  /**
   * Clears the session by hitting the backend logout endpoint, which replies with a Set-Cookie that
   * expires the Domain=.lazo.one lazo_auth_token cookie. Sent in credentials mode for the same reason
   * as /login and /session — a cross-origin XHR (app.lazo.one -> api.lazo.one) only sends the cookie
   * and honours the expiring Set-Cookie response when withCredentials is set. Without it the browser
   * silently drops the response cookie and the token survives the logout.
   */
  logout(): Promise<ApiResponseV2<void>> {
    return HttpClientV2.post(`${this.path}/logout`, {}, LOGIN_AUTH_CONFIG);
  }

  getUserByToken(token: string): Promise<ApiResponseV2<User>> {
    return HttpClientV2.get(`${this.path}/user/token?t=${token}`, {
      axiosConfig: {
        ...(IS_DEV ? { baseURL: "" } : {}),
        timeout: 25000,
      },
    });
  }
}

export default new LazoAuthService("/api/lazo-auth");
